import * as React from "react";
import {
  AppContext,
  ApplicationProvider,
  ArtefactMode,
  BreadcrumbsContext,
  Environments,
  Partition,
  SelectedArtefactType
} from "@emisgroup/cohort-builder-kit";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "../../../AuthContext";
import { IRootState } from "../../../reducer/CombineReducer";
import { ExtractListSlice } from "../ExtractList";
import { IPartitionInput } from "./redux/Types";
import { GetPartitionList } from "./Service";
import { PartitionListSlice } from "./index";

export interface PartitionInputProps {
  /**
   * Data source for this component
   */
  artefactId: string;
}

/**
 * Responsible for rendering the Extract List
 */
export const PartitionList = (props: PartitionInputProps): JSX.Element => {
  const { artefactId } = props;
  const { artefacts } = useSelector((state: IRootState) => state.partition);
  const dispatch = useDispatch();
  const { user } = useUser();
  const { items, selectedId } = React.useContext(BreadcrumbsContext);
  const { parentPartitionData, metaData } = React.useContext(AppContext);

  const getRequestParam = (): IPartitionInput => {
    return {
      artefactId: artefactId
    };
  };

  /**
   * Set the breadcrumb items
   */
  React.useEffect(() => {
    dispatch(PartitionListSlice.actions.setBreadcrumbItems(items));
    dispatch(ExtractListSlice.actions.setDashBoardName(parentPartitionData?.name as string));
  }, [items]);

  React.useEffect(() => {
    dispatch(PartitionListSlice.actions.setParentData(parentPartitionData));
  }, [parentPartitionData]);

  /**
   * Set the id from the  breadcrumb selected
   */
  React.useEffect(() => {
    dispatch(PartitionListSlice.actions.setSelectedId(selectedId));
    dispatch(PartitionListSlice.actions.setMetaData(metaData));
    const selectedPartition = artefacts?.find((x: SelectedArtefactType) => x?.id === selectedId);
    dispatch(ExtractListSlice.actions.setDashBoardName(selectedPartition?.name as string));
  }, [selectedId]);

  /**
   * Extract api on change of financial year change
   */
  React.useEffect(() => {
    dispatch(PartitionListSlice.actions.setPartitionList([]));
    if (artefactId) {
      GetPartitionList(dispatch, getRequestParam());
    }
  }, [artefactId]);

  /**
   * Set selected artefact list
   * @param data provided input data
   */
  const setArtefactList = (data: SelectedArtefactType[]): void => {
    dispatch(PartitionListSlice.actions.setPartitionList(data));
    const filteredPartitionList = data.filter((x: SelectedArtefactType) => x.level === 1);
    if (filteredPartitionList?.length > 0) {
      dispatch(PartitionListSlice.actions.setSelectedId(filteredPartitionList?.[0]?.id));
    } else {
      dispatch(PartitionListSlice.actions.setSelectedId(""));
    }
  };

  /**
   * Open the extract details
   * @param id - partition id to fetch corresponding extract details
   */
  const openExtractDetails = (id: string): void => {
    dispatch(ExtractListSlice.actions.setSelectedExtractGuid(id));
    dispatch(ExtractListSlice.actions.setExtractDetailView(true));
    const data = artefacts?.find((x: SelectedArtefactType) => x?.id === id);
    dispatch(ExtractListSlice.actions.setDashBoardName(data?.name as string));
    setTimeout(() => {
      dispatch(ExtractListSlice.actions.setExtractDetailView(false));
    }, 500);
  };

  return (
    <>
      <ApplicationProvider>
        <Partition
          partitionList={artefacts}
          accessToken={user.token}
          env={process.env.APP_TARGET_ENV as Environments}
          mode={ArtefactMode.ASSESSMENT}
          openExtractDetails={openExtractDetails}
          setPartitionList={setArtefactList}
        />
      </ApplicationProvider>
    </>
  );
};
